import { computed } from '@ember/object';
import { equal, notEmpty, reads } from '@ember/object/computed';
import { isPresent } from '@ember/utils';
import { attr, belongsTo, hasMany } from '@ember-data/model';
import Money from '@square/dashboard-money/models/money';
import NumericEnum from '@square/dashboard-utilities/utils/numeric-enum';
import ItemsV3Base from '@square/dashboard-items-data/models/items-v3-base';
import { EnablingActionType as EnablingActionTypeProto } from '@square/dashboard-protos/models/squareup/items/rule_types';
const MAX_NAME_LENGTH = 255;
const EnablingActionType = NumericEnum.create(['UNKNOWN_ENABLING_ACTION_TYPE', 'DISABLE', 'ADJUSTMENT']);
const DiningOptionPredicate = NumericEnum.create(['UNKNOWN', 'IN']);
const TaxExemptionOptions = NumericEnum.create(['MAX_ITEM_PRICE', 'MAX_TOTAL_AMOUNT', 'DINING_OPTION', 'NO_CONDITION']);
const ObjectPredicateType = NumericEnum.create(['UNKNOWN', 'IN']);
const ObjectFilter = NumericEnum.create(['UNKNOWN_OBJECT_FILTER', 'ALL', 'INCLUDE', 'EXCLUDE']);
export const ENABLING_ACTION_TYPE_MAPPING = {
  [EnablingActionTypeProto.ADJUSTMENT]: EnablingActionType.ADJUSTMENT,
  [EnablingActionTypeProto.DISABLE]: EnablingActionType.DISABLE
};
export default ItemsV3Base.extend({
  enabledUnitsAll: attr('boolean', {
    defaultValue: false
  }),
  name: attr('string'),
  actionType: attr('enum', {
    enumClass: EnablingActionType
  }),
  includeAllItems: equal('itemConfigFilter', ObjectFilter.ALL),
  // Related cogs objects
  tax: belongsTo('fee', {
    async: false
  }),
  items: hasMany('item', {
    async: false
  }),
  productSet: belongsTo('product-set', {
    async: false
  }),
  adjustmentTax: belongsTo('fee', {
    async: false,
    inverse: null
  }),
  diningOptions: hasMany('dining-option', {
    async: false
  }),
  price: attr('tax-rule-legacy-money'),
  maxTotalAmount: attr('number'),
  maxItemPrice: attr('number'),
  // Quantity-based conditions
  quantityGreaterThan: attr('number'),
  quantityLessThan: attr('number'),
  quantityEqualsTo: attr('number'),
  includeCustomAmounts: attr('boolean', {
    allowNull: true
  }),
  taxConfigFilter: attr('enum', {
    enumClass: ObjectFilter
  }),
  itemConfigFilter: attr('enum', {
    enumClass: ObjectFilter
  }),
  diningOptionPredicateType: attr('enum', {
    enumClass: ObjectPredicateType
  }),
  allowedSubunitsForDiningOption: computed('tax', 'subunits.[]', function () {
    if (!this.tax) {
      return [];
    }
    return this.subunits;
  }),
  // This should only be used for tax rules with a single condition.
  // For tax rules with multiple conditions, use the applicable
  // computed property below (either exemptedAmountMaxTotalAmount or
  // exemptedAmountMaxItemPrice).
  exemptedAmount: computed('exemptedAmountMaxItemPrice', 'exemptedAmountMaxTotalAmount', function () {
    return this.exemptedAmountMaxTotalAmount ?? this.exemptedAmountMaxItemPrice ?? null;
  }),
  exemptedAmountMaxTotalAmount: computed('maxTotalAmount', 'price.currencyCode', function () {
    return this.maxTotalAmount && this.price?.currencyCode ? Money.create({
      amount: this.maxTotalAmount,
      currencyCode: this.price.currencyCode
    }) : null;
  }),
  exemptedAmountMaxItemPrice: computed('maxItemPrice', 'price.currencyCode', function () {
    return this.maxItemPrice && this.price?.currencyCode ? Money.create({
      amount: this.maxItemPrice,
      currencyCode: this.price.currencyCode
    }) : null;
  }),
  hasValidTaxes: notEmpty('tax'),
  hasValidDiningOptions: notEmpty('diningOptions'),
  isDirty: computed('hasDirtyFields', 'productSet.isDirty', function (...args) {
    if (this.hasDirtyFields) {
      return true;
    }
    if (this.productSet?.isDirty) {
      return true;
    }
    return this._super(...args);
  }),
  // Helper properties

  hasValidItems: computed('includeAllItems', 'includeCustomAmounts', 'items.length', 'productSet.productsAreSelected', function () {
    if (this.includeAllItems) {
      return true;
    }
    if (isPresent(this.productSet)) {
      return this.productSet.productsAreSelected;
    }
    return this.items?.length > 0 || this.includeCustomAmounts;
  }),
  relationshipTrackerVersion: reads('version'),
  // For safer backwards compatibility, don't include
  // quantity conditions here.
  conditionType: computed('diningOptionPredicateType', 'maxItemPrice', 'maxTotalAmount', 'quantityEqualsTo', 'quantityGreaterThan', 'quantityLessThan', function () {
    if (this.diningOptionPredicateType) {
      return 'diningOption';
    }
    if (isPresent(this.maxItemPrice)) {
      return 'maxItemPrice';
    }
    if (isPresent(this.maxTotalAmount)) {
      return 'maxTotalAmount';
    }
    if (isPresent(this.quantityEqualsTo)) {
      return 'quantityEqualsTo';
    }
    if (isPresent(this.quantityGreaterThan)) {
      return 'quantityGreaterThan';
    }
    if (isPresent(this.quantityLessThan)) {
      return 'quantityLessThan';
    }
    return null;
  }),
  conditionTypes: computed('diningOptionPredicateType', 'maxItemPrice', 'maxTotalAmount', 'quantityEqualsTo', 'quantityGreaterThan', 'quantityLessThan', function () {
    const conditionTypes = [];
    if (this.diningOptionPredicateType) {
      conditionTypes.push('diningOption');
    }
    if (isPresent(this.maxItemPrice) && this.maxItemPrice !== 0) {
      conditionTypes.push('maxItemPrice');
    }
    if (isPresent(this.maxTotalAmount) && this.maxTotalAmount !== 0) {
      conditionTypes.push('maxTotalAmount');
    }
    if (isPresent(this.quantityEqualsTo)) {
      conditionTypes.push('quantityEqualsTo');
    }
    if (isPresent(this.quantityGreaterThan)) {
      conditionTypes.push('quantityGreaterThan');
    }
    if (isPresent(this.quantityLessThan)) {
      conditionTypes.push('quantityLessThan');
    }
    return conditionTypes;
  }),
  // Passes an empty array to clear dining options.
  updateDiningOptions(diningOptions = []) {
    this.watchRelationship('diningOptions', () => {
      this.diningOptions.setObjects(diningOptions);
    });
  },
  updateGloballyEnabledForRelationships(globallyEnabled) {
    if (isPresent(this.productSet)) {
      this.productSet.set('globallyEnabled', globallyEnabled);
    }
  },
  addAffectedRelatedObjects(subunitToken) {
    const subunitToAdd = this.subunits.findBy('token', subunitToken);
    if (isPresent(subunitToAdd)) {
      if (isPresent(this.productSet)) {
        this.productSet.addMerchantSubunit(subunitToAdd);
      }
    }
  },
  removeAffectedRelatedObjects(subunitToken) {
    if (isPresent(this.productSet)) {
      this.productSet.removeMerchantSubunit(subunitToken);
    }
  },
  clearAllExemptions() {
    this.set('diningOptionPredicateType', null);
    this.set('price', null);
    this.set('maxTotalAmount', null);
    this.set('maxItemPrice', null);
    this.set('quantityLessThan', null);
    this.set('quantityGreaterThan', null);
    this.set('quantityEqualsTo', null);
    this.updateDiningOptions([]);
  },
  clearAdjustmentTax() {
    this.set('adjustmentTax', null);
  },
  updateExemptionAmount(property, value) {
    if (value) {
      this.set(property, value._amount);
      this.set('price', Money.create({
        currencyCode: value.currencyCode
      }));
    } else {
      this.set(property, null);
      this.set('price', null);
    }
  },
  updateTaxExemptionName(name) {
    this.set('name', name);
  },
  clearItemSelections() {
    this.items.setObjects([]);
    this.set('itemConfigFilter', undefined);
    this.set('includeCustomAmounts', false);
    if (isPresent(this.productSet)) {
      this.productSet.clear();
      this.productSet.set('matchAllProducts', false);
    }
  },
  // Cribbed from fee model
  productSetMembershipForItem(item) {
    if (!this.productSet) {
      return false;
    }
    if (this.productSet.matchAllProducts) {
      return true;
    }
    return this.productSet.items.includes(item) || this.productSet.categories.includes(item.menuCategory);
  },
  cascadeGloballyEnabled() {
    if (isPresent(this.productSet)) {
      this.productSet.set('globallyEnabled', this.globallyEnabled);
      this.productSet.watchRelationship('enabledUnits', () => {
        this.productSet.set('enabledUnits', this.enabledUnits);
      });
      this.productSet.watchRelationship('disabledUnits', () => {
        this.productSet.set('disabledUnits', this.disabledUnits);
      });
    }
  },
  revert() {
    this.rollbackAttributes();
  }
}).reopenClass({
  MAX_NAME_LENGTH,
  EnablingActionType,
  DiningOptionPredicate,
  TaxExemptionOptions,
  ObjectPredicateType,
  ObjectFilter,
  catalogType() {
    return 'TAX_RULE';
  },
  sortKey: 'name',
  createDefaultOptions() {
    return {
      actionType: EnablingActionType.DISABLE,
      dining_option_id: [],
      taxConfigFilter: ObjectFilter.INCLUDE,
      fee_id: null,
      item_id: [],
      includeCustomAmounts: true,
      adjustment_fee_token: null
    };
  },
  _modelNameForMigration: 'tax-rule'
});